<template>
  <CustomModal
    title="Group Details"
    width="34vw"
    :visible_sections="{
      header: true,
      body: true,
      footer: false,
    }"
    v-model:visible="internalVisible"
  >
    <div id="group_details_modal" class="flex flex-col min-h-[55vh]">
      <CustomTabs
        v-model="activeTab"
        :tabs="tabs"
        tabHeaderClass="max-w-[150px]"
      >
        <template #tab_content-0>
          <div class="flex flex-col gap-6 max-h-[45vh] overflow-y-auto">
            <div
              v-for="(detail, index) in groupDetails"
              :key="`detail_${index}`"
              class="flex flex-col gap-1"
            >
              <span class="text-[13px] secondary_color">{{
                detail.label
              }}</span>
              <span class="text-sm font-medium primary_color">
                {{ detail.value }}
              </span>
            </div>

            <div v-if="groupDescription" class="flex flex-col gap-1">
              <span class="text-[13px] secondary_color">Description</span>
              <span class="text-sm primary_color whitespace-pre-wrap" v-html="linkifyDescription"></span>
            </div>

            <div v-if="isExternalGroup && inviteLink" class="flex flex-col gap-1">
              <span class="text-[13px] secondary_color">Invite members</span>
              <a-button
                class="secondary_small w-fit"
                @click="handleCopyInviteLink"
              >
                Copy invite link
              </a-button>
            </div>

            <a-button
              v-if="canLeaveGroup"
              class="danger_small w-fit shrink-0"
              :loading="leavingGroup"
              @click="handleLeaveGroup"
            >
              Leave Group
            </a-button>
          </div>
        </template>

        <template #tab_content-1>
          <div class="flex flex-col gap-4">
            <div v-if="!isOneOnOneDM" class="flex flex-col gap-2">
              <span class="text-[13px] secondary_color">Add Members</span>

              <div class="flex flex-col gap-2">
                <div class="flex flex-row gap-2 items-center">
                  <a-select
                    v-model:value="selectedUserIds"
                    :mode="isExternalGroup ? 'tags' : 'multiple'"
                    :placeholder="isExternalGroup ? 'Search users or type email addresses to invite' : 'Search and select users'"
                    class="flex-1 member-select"
                    :options="availableUserOptions"
                    show-search
                    allow-clear
                    option-filter-prop="label"
                    :token-separators="isExternalGroup ? [',', ' '] : []"
                    :maxTagCount="10"
                    :maxTagPlaceholder="(omittedValues) => `+${omittedValues.length} more`"
                  />
                  <a-button
                    class="primary_medium"
                    :disabled="selectedUserIds.length === 0"
                    :loading="addingMember"
                    @click="handleAddMembers"
                  >
                    Add
                  </a-button>
                </div>
                <a-checkbox
                  v-if="availableUserOptions.length > 0 && !memberPagination.hasMore"
                  :checked="isAllSelected"
                  :indeterminate="isPartiallySelected"
                  @change="handleSelectAll"
                >
                  <span class="text-xs secondary_color">Select all users ({{ availableUserOptions.length }})</span>
                </a-checkbox>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <div
                v-if="loadingMembers"
                class="w-full h-full flex flex-col items-center justify-center"
              >
                <CustomLoader text="Loading members" />
              </div>

              <EmptyScreen
                v-else-if="members.length === 0 && pendingInvites.length === 0"
                source="group_details_modal"
                text="No members found"
              />

              <div
                v-else
                ref="memberScrollContainer"
                class="flex flex-col gap-1 max-h-[300px] overflow-y-auto"
                @scroll="handleMemberScroll"
              >
                <div
                  v-for="(member, index) in members"
                  :key="`${member?.id}_member_${index}`"
                  class="flex flex-row items-center justify-between px-2 py-2 rounded-lg"
                >
                  <div class="flex flex-row items-center gap-2">
                    <ProfileIcon :data="enrichWithStoreUser(member.user)" :font="14" :size="32" />
                    <div class="flex flex-col">
                      <span class="text-sm primary_color">
                        {{ getMemberName(member) }}
                        <span
                          v-if="isCurrentUser(member.user_id)"
                          class="secondary_color"
                        >(You)</span>
                        <span
                          v-if="isMemberAdmin(member)"
                          class="secondary_color"
                        >(Admin)</span>
                      </span>
                      <span class="text-xs secondary_color truncate">
                        {{ member.user?.email || "No email" }}
                      </span>
                    </div>
                  </div>

                  <a-button
                    v-if="!isCurrentUser(member.user_id) && isAdmin"
                    class="danger_small"
                    :loading="removingMemberId === member.user_id"
                    @click="handleRemoveMember(member)"
                  >
                    Remove
                  </a-button>
                </div>

                <div
                  v-for="(invite, idx) in pendingInvites"
                  :key="`pending_invite_${invite.email}_${idx}`"
                  class="flex flex-row items-center justify-between px-2 py-2 rounded-lg"
                >
                  <div class="flex flex-row items-center gap-2">
                    <ProfileIcon :data="{ email: invite.email, name: invite.email }" :font="14" :size="32" />
                    <div class="flex flex-col">
                      <span class="text-sm primary_color">
                        {{ invite.email }}
                        <span class="secondary_color">(Invited)</span>
                      </span>
                      <span class="text-xs secondary_color truncate">
                        Invitation sent
                      </span>
                    </div>
                  </div>

                  <a-button
                    v-if="isAdmin"
                    class="danger_small"
                    :loading="revokingEmail === invite.email"
                    @click="handleRevokeInvite(invite)"
                  >
                    Revoke
                  </a-button>
                </div>

                <div
                  v-if="loadingMoreMembers"
                  class="flex items-center justify-center py-2"
                >
                  <CustomLoader text="Loading more members" />
                </div>
              </div>
            </div>
          </div>
        </template>
      </CustomTabs>
    </div>
  </CustomModal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { renderEntity } from "@/src/utils/utils.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import { formatTypes } from "@/src/constants/dateTimeConstants.js";
import { chatTypes, chatRecipientTypes, HOME_GROUP_NAME } from "@/src/constants/chatConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import store from "@/src/store/index.js";
import ChatService from "@/src/api/ChatService.js";
import DashboardService from "@/src/api/DashboardService.js";
import { formatLinksInsideString } from "@/src/utils/htmlUtils.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import CustomTabs from "@/src/components/CustomTabs.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const props = defineProps({
  visible: { type: Boolean, required: true },
  group: { type: Object, default: null },
  initialTab: { type: Number, default: 0 },
});

const emit = defineEmits(["update:visible"]);

const internalVisible = ref(props.visible);
const activeTab = ref(props.initialTab);
const members = ref([]);
const loadingMembers = ref(false);
const loadingMoreMembers = ref(false);
const addingMember = ref(false);
const removingMemberId = ref(null);
const revokingEmail = ref(null);
const leavingGroup = ref(false);
const selectedUserIds = ref([]);
const memberScrollContainer = ref(null);
const allMemberUserIds = ref(new Set());
const pendingInvites = ref([]);
const memberPagination = ref({
  limit: 20,
  offset: 0,
  hasMore: false,
  total: 0,
});

const tabs = computed(() => [
  { id: "about", title: "About" },
  { id: "members", title: `Members${memberPagination.value.total > 0 ? ` (${memberPagination.value.total})` : ''}` },
]);

const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const allUsers = computed(() => store.getters["users/users"] || []);

const enrichWithStoreUser = (user) => {
  if (!user?.id) return user;
  const found = allUsers.value.find((u) => u.id === user.id);
  return found || user;
};

const isDM = computed(() => {
  return (
    props.group?.is_dm === true ||
    props.group?.type === chatTypes.DM ||
    props.group?.type === chatTypes.SELF_DM ||
    props.group?.type === chatTypes.GROUP_DM
  );
});

const isOneOnOneDM = computed(() => {
  return (
    props.group?.is_dm === true &&
    (props.group?.type === chatTypes.DM || props.group?.type === chatTypes.SELF_DM) &&
    props.group?.recipient_type === chatRecipientTypes.ONE_ON_ONE
  );
});

const availableUserOptions = computed(() => {
  return allUsers.value
    .filter((user) => !allMemberUserIds.value.has(Number(user.id)))
    .map((user) => ({
      value: user.id,
      label: renderEntity(user),
    }));
});

const isAllSelected = computed(() => {
  return availableUserOptions.value.length > 0 &&
    selectedUserIds.value.length === availableUserOptions.value.length;
});

const isPartiallySelected = computed(() => {
  return selectedUserIds.value.length > 0 &&
    selectedUserIds.value.length < availableUserOptions.value.length;
});

const handleSelectAll = (e) => {
  if (e.target.checked) {
    selectedUserIds.value = availableUserOptions.value.map((u) => u.value);
  } else {
    selectedUserIds.value = [];
  }
};

const formattedCreatedAt = computed(() => {
  if (!props.group?.created_at) return "";
  return formatTimestamp(props.group.created_at, "date", formatTypes.timestamp);
});

const creatorName = computed(() => {
  if (props.group?.creator?.name) return props.group.creator.name;
  if (props.group?.creator?.first_name) {
    return `${props.group.creator.first_name} ${props.group.creator.last_name || ""}`.trim();
  }
  const admin = members.value.find((m) => isMemberAdmin(m));
  if (admin) return getMemberName(admin);
  return null;
});

const createdByValue = computed(() => {
  const name = creatorName.value;
  const date = formattedCreatedAt.value;
  if (name && date) return `${name} on ${date}`;
  if (name) return name;
  return null;
});

const createdOnValue = computed(() => {
  return formattedCreatedAt.value || "Unknown";
});

const displayName = computed(() => {
  if (!props.group) return "Unknown";
  return props.group.name || "Unknown";
});

const groupDescription = computed(() => {
  return props.group?.description?.trim() || null;
});

const linkifyDescription = computed(() => {
  if (!groupDescription.value) return "";
  return formatLinksInsideString(groupDescription.value);
});

const isExternalGroup = computed(() => {
  return props.group?.type === "external";
});

const inviteLink = computed(() => {
  const link = props.group?.invite_link;
  if (!link) return null;
  if (link.startsWith('http')) return link;
  return `${window.location.origin}${link.startsWith('/') ? '' : '/'}${link}`;
});

const handleCopyInviteLink = async () => {
  try {
    await navigator.clipboard.writeText(inviteLink.value);
    message.success("Invite link copied!");

    // Register the invite token mapping so it can be resolved during sign-up
    const link = props.group?.invite_link;
    if (link) {
      const tokenMatch = link.match(/\/invite\/([A-Za-z0-9_-]+)/);
      if (tokenMatch && props.group?.workspace_id && props.group?.id) {
        DashboardService.registerExternalInviteToken(
          tokenMatch[1],
          props.group.workspace_id,
          props.group.id,
        ).catch(() => {});
      }
    }
  } catch {
    message.error("Failed to copy link");
  }
};

const groupDetails = computed(() => {
  const details = [
    { label: "Channel Name", value: displayName.value },
  ];
  if (createdByValue.value) {
    details.push({ label: "Created by", value: createdByValue.value });
  } else {
    details.push({ label: "Created on", value: createdOnValue.value });
  }
  return details;
});

const getMemberName = (member) => {
  if (!member?.user) return "Unknown";
  const user = member.user;
  return (
    user.name ||
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.email ||
    "Unknown"
  );
};

const isCurrentUser = (userId) => {
  return currentUserId.value === userId;
};

const isMemberAdmin = (member) => {
  if (!member?.role) return false;
  const role = member.role.toUpperCase();
  return role === "ADMIN" || role === "OWNER";
};

const currentUserRoleRef = ref(null);

const fetchCurrentUserRole = async () => {
  try {
    if (!props.group?.id || !currentUserId.value) return;
    let offset = 0;
    const limit = 50;
    while (true) {
      const response = await ChatService.fetchGroupMembers(props.group.id, { limit, offset });
      if (!response?.success || !response?.members) break;
      const found = response.members.find(
        (m) => Number(m.user_id) === Number(currentUserId.value),
      );
      if (found) {
        currentUserRoleRef.value = found.role || null;
        return;
      }
      if (!response.pagination?.has_more) break;
      offset += limit;
    }
    currentUserRoleRef.value = null;
  } catch (error) {
    console.error("An error occurred while fetching current user role: ", error);
    currentUserRoleRef.value = null;
  }
};

const currentUserRole = computed(() => currentUserRoleRef.value);

const isAdmin = computed(() => {
  if (!currentUserRole.value) return false;
  const role = currentUserRole.value.toUpperCase();
  return role === "ADMIN" || role === "OWNER";
});

const canLeaveGroup = computed(() => {
  if (isOneOnOneDM.value) return false;
  if (props.group?.name === HOME_GROUP_NAME) return false;
  if (!currentUserRole.value) return false;
  const role = currentUserRole.value.toUpperCase();
  return role !== "ADMIN" && role !== "OWNER";
});

const handleLeaveGroup = async () => {
  try {
    if (!props.group?.id || !currentUserId.value) return;

    leavingGroup.value = true;

    const response = await ChatService.removeGroupMember(
      props.group.id,
      currentUserId.value,
    );

    if (response?.success) {
      message.success("You have left the group");
      internalVisible.value = false;
      store.dispatch("chats/removeGroup", props.group.id);

      const groups = store.getters["chats/groups"] || [];
      if (groups.length > 0) {
        store.dispatch("chats/setActiveChat", groups[0]);
      } else {
        store.dispatch("chats/setActiveChat", null);
      }

      eventBus.emit("group-updated");
    } else {
      message.error(response?.error || "Failed to leave group");
    }
  } catch (error) {
    console.error("An error occurred while leaving group: ", error);
    message.error("Failed to leave group");
  } finally {
    leavingGroup.value = false;
  }
};

const fetchAllMemberIds = async () => {
  try {
    if (!props.group?.id) return;
    const ids = new Set();
    let offset = 0;
    const limit = 50;
    while (true) {
      const response = await ChatService.fetchGroupMembers(props.group.id, { limit, offset });
      if (!response?.success || !response?.members) break;
      response.members.forEach((m) => ids.add(Number(m.user_id)));
      if (!response.pagination?.has_more) break;
      offset += limit;
    }
    allMemberUserIds.value = ids;
  } catch (error) {
    console.error("An error occurred while fetching all member IDs: ", error);
  }
};

const fetchPendingInvites = async () => {
  if (!props.group?.id || !isExternalGroup.value) {
    pendingInvites.value = [];
    return;
  }
  try {
    const response = await DashboardService.fetchExternalGroupPendingInvites(props.group.id);
    if (response?.success) {
      pendingInvites.value = response.invites || [];
    }
  } catch (error) {
    console.error("An error occurred while fetching pending invites: ", error);
    pendingInvites.value = [];
  }
};

const fetchMembers = async (params = {}) => {
  try {
    if (!props.group?.id) return;

    const isLoadingMore = params.loadMore;

    if (isLoadingMore) {
      loadingMoreMembers.value = true;
    } else {
      loadingMembers.value = true;
      memberPagination.value.offset = 0;
      members.value = [];
    }

    const queryParams = {
      limit: memberPagination.value.limit,
      offset: isLoadingMore ? memberPagination.value.offset : 0,
    };

    const response = await ChatService.fetchGroupMembers(props.group.id, queryParams);

    if (response?.success && response?.members) {
      if (isLoadingMore) {
        members.value = [...members.value, ...response.members];
      } else {
        members.value = response.members;
      }

      memberPagination.value.offset = isLoadingMore
        ? memberPagination.value.offset + memberPagination.value.limit
        : memberPagination.value.limit;
      memberPagination.value.hasMore = response?.pagination?.has_more ?? false;
      memberPagination.value.total = response?.pagination?.total_count ?? 0;
    }
  } catch (error) {
    console.error("An error occurred while fetching members: ", error);
    message.error("Failed to load members");
  } finally {
    loadingMembers.value = false;
    loadingMoreMembers.value = false;
  }
};

const handleMemberScroll = (event) => {
  const container = event.target;
  const { scrollTop, scrollHeight, clientHeight } = container;
  const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

  if (isNearBottom && !loadingMoreMembers.value && !loadingMembers.value && memberPagination.value.hasMore) {
    fetchMembers({ loadMore: true });
  }
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const handleAddMembers = async () => {
  try {
    if (!props.group?.id || selectedUserIds.value.length === 0) return;

    // Split selected entries into user IDs (numeric, from dropdown) and free-form emails
    const newUserIds = [];
    const emailsToInvite = [];
    for (const entry of selectedUserIds.value) {
      if (typeof entry === "number" || (/^\d+$/.test(String(entry)))) {
        const idNum = Number(entry);
        if (!allMemberUserIds.value.has(idNum)) newUserIds.push(idNum);
      } else if (typeof entry === "string" && EMAIL_REGEX.test(entry.trim())) {
        emailsToInvite.push(entry.trim().toLowerCase());
      }
    }

    if (newUserIds.length === 0 && emailsToInvite.length === 0) {
      selectedUserIds.value = [];
      return;
    }

    addingMember.value = true;

    // Add existing workspace users directly via chat service
    for (const userId of newUserIds) {
      const response = await ChatService.addGroupMember(
        props.group.id,
        userId,
        "MEMBER",
      );

      if (!response?.success) {
        message.error("Failed to add member");
      } else {
        const addedUser = allUsers.value.find(u => u.id === userId);
        if (addedUser) {
          const userName = addedUser.name ||
                          `${addedUser.first_name || ''} ${addedUser.last_name || ''}`.trim() ||
                          addedUser.email ||
                          'Unknown User';

          const msgResponse = await ChatService.createMessage({
            recipient_type: chatRecipientTypes.GROUP,
            recipient_id: Number(props.group.id),
            thread_id: null,
            text: `added ${userName}`,
            attachments: [],
            mentions: [],
            mention_groups: [],
            system_message: true,
            is_system: true,
          });

          if (msgResponse?.success && msgResponse?.message) {
            store.dispatch("chats/addMessage", msgResponse.message);
            store.commit("chats/ADD_PENDING_MESSAGE_ID", msgResponse.message.id);
            eventBus.emit("message-sent");

            await store.dispatch("chats/updateSidebarGroup", {
              groupId: props.group.id,
              lastMessage: msgResponse.message.text,
              senderId: msgResponse.message.user_id || msgResponse.message.sender?.id,
            });
          }
        }
      }
    }

    // Send email invites for free-form emails (external groups only)
    if (emailsToInvite.length > 0 && isExternalGroup.value) {
      try {
        const inviteResponse = await DashboardService.inviteExternalGroupByEmail(
          props.group.id,
          emailsToInvite,
        );
        if (inviteResponse?.success) {
          const invitedCount = (inviteResponse.results || []).filter(r => r.status === "invited").length;
          const addedCount = (inviteResponse.results || []).filter(r => r.status === "added").length;
          const failedCount = (inviteResponse.results || []).filter(r => r.status === "failed").length;
          const alreadyMemberCount = (inviteResponse.results || []).filter(r => r.status === "already_member").length;

          if (invitedCount > 0) message.success(`Invitation email sent to ${invitedCount} recipient${invitedCount > 1 ? "s" : ""}`);
          if (addedCount > 0) message.success(`${addedCount} existing user${addedCount > 1 ? "s" : ""} added to the group`);
          if (alreadyMemberCount > 0) message.info(`${alreadyMemberCount} user${alreadyMemberCount > 1 ? "s are" : " is"} already a member`);
          if (failedCount > 0) message.error(`Failed to invite ${failedCount} email${failedCount > 1 ? "s" : ""}`);
        } else {
          message.error(inviteResponse?.error || "Failed to send email invitations");
        }
      } catch (err) {
        console.error("Failed to invite emails:", err);
        message.error("Failed to send email invitations");
      }
    } else if (emailsToInvite.length > 0 && !isExternalGroup.value) {
      message.warning("Email invitations are only available for external groups");
    }

    if (newUserIds.length > 0) {
      message.success("Members added successfully");
    }

    selectedUserIds.value = [];
    await fetchMembers();
    await fetchAllMemberIds();
    await fetchPendingInvites();
    eventBus.emit("group-members-updated", props.group?.id);
  } catch (error) {
    console.error("An error occurred while adding members: ", error);
    message.error("Failed to add members");
  } finally {
    addingMember.value = false;
  }
};

const handleRemoveMember = async (member) => {
  try {
    if (!props.group?.id || !member.user_id) return;

    removingMemberId.value = member.user_id;

    const response = await ChatService.removeGroupMember(
      props.group.id,
      member.user_id,
    );

    if (response?.success) {
      const userName = getMemberName(member);
      
      const messageResponse = await ChatService.createMessage({
        recipient_type: chatRecipientTypes.GROUP,
        recipient_id: Number(props.group.id),
        thread_id: null,
        text: `removed ${userName}`,
        attachments: [],
        mentions: [],
        mention_groups: [],
        system_message: true,
        is_system: true,
      });

      if (messageResponse?.success && messageResponse?.message) {
        store.dispatch("chats/addMessage", messageResponse.message);
        store.commit("chats/ADD_PENDING_MESSAGE_ID", messageResponse.message.id);
        eventBus.emit("message-sent");
        
        await store.dispatch("chats/updateSidebarGroup", {
          groupId: props.group.id,
          lastMessage: messageResponse.message.text,
          senderId: messageResponse.message.user_id || messageResponse.message.sender?.id,
        });
      }

      message.success("Member removed successfully");
      await fetchMembers();
      await fetchAllMemberIds();
      eventBus.emit("group-members-updated", props.group?.id);
    } else {
      message.error("Failed to remove member");
    }
  } catch (error) {
    console.error("An error occurred while removing member: ", error);
    message.error("Failed to remove member");
  } finally {
    removingMemberId.value = null;
  }
};

const handleRevokeInvite = async (invite) => {
  try {
    if (!props.group?.id || !invite.email) return;

    revokingEmail.value = invite.email;

    const response = await DashboardService.revokeExternalGroupInvite(
      props.group.id,
      invite.email,
    );

    if (response?.success) {
      message.success("Invite revoked successfully");
      pendingInvites.value = pendingInvites.value.filter(
        (i) => i.email !== invite.email,
      );
    } else {
      message.error(response?.error || "Failed to revoke invite");
    }
  } catch (error) {
    console.error("An error occurred while revoking invite: ", error);
    message.error("Failed to revoke invite");
  } finally {
    revokingEmail.value = null;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      activeTab.value = props.initialTab;
      selectedUserIds.value = [];
      currentUserRoleRef.value = null;
      allMemberUserIds.value = new Set();
      pendingInvites.value = [];
      fetchMembers();
      fetchAllMemberIds();
      fetchCurrentUserRole();
      fetchPendingInvites();

      if (
        !store.getters["users/users"] ||
        store.getters["users/users"].length === 0
      ) {
        store.dispatch("users/fetchUsers");
      }
    }
  },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
  },
);
</script>

<style scoped>
.member-select :deep(.ant-select-selector) {
  max-height: 120px;
  overflow-y: auto !important;
}
</style>
